import { Typography } from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import { formatDate as formatDateDisplay, formatCurrency } from '../../../utils/formatUtils';

const parseDate = (dateString) => new Date(dateString);

/**
 * Calcula el interés entre dos fechas
 * @param {number} principal - Monto principal
 * @param {string} dueDate1 - Primera fecha de vencimiento
 * @param {string} dueDate2 - Segunda fecha de vencimiento
 * @returns {Object} - Objeto con interest y total
 */
export const calculateInterest = (principal, dueDate1, dueDate2) => {
  const date1 = parseDate(dueDate1);
  const date2 = parseDate(dueDate2);
  const diffTime = Math.abs(date2 - date1);
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  const total = principal * (1 + (0.09 * diffDays) / 30);
  const interest = total - principal;

  return {
    interest: Math.ceil(interest).toFixed(2),
    total: Math.ceil(total).toFixed(2),
  };
};

/**
 * Transforma los datos de cupones para su visualización en la tabla
 * @param {Array} coupons - Array de cupones desde la API
 * @returns {Array} - Array de cupones transformados
 */
export const transformCouponsData = (coupons) => {
  return coupons.map((coupon) => {
    // Determinar el tipo de cuota de forma más legible
    let feeTypeName;
    
    if (coupon.is_adjudication) {
      feeTypeName = 'Adjudicación';
    } else if (coupon.is_score) {
      feeTypeName = 'Puntaje';
    } else if (coupon.is_advance_payment) {
      feeTypeName = 'Adelanto';
    } else if (coupon.fee_number1 === 0) {
      feeTypeName = 'Adecuación';
    } else {
      // Cuotas normales
      const cuotaNumero = coupon.fee_number1;
      const cuotaHasta = coupon.fee_number2;
      
      if (cuotaHasta && cuotaHasta !== 0 && cuotaHasta !== null && cuotaHasta !== undefined) {
        feeTypeName = `${cuotaNumero} - ${cuotaHasta}`;
      } else {
        feeTypeName = cuotaNumero.toString();
      }
    }

    const cuotas = feeTypeName;

    let importes;
    if (feeTypeName === 'Adjudicación' || feeTypeName === 'Adelanto') {
      const formattedDate = formatDateDisplay(coupon.first_due_date);
      importes = [`Venc: ${formattedDate} ${formatCurrency(coupon.first_due_amount)}`];
    } else {
      importes = [
        `1\u00B0 vencimiento: ${formatDateDisplay(coupon.first_due_date)} | ${formatCurrency(
          coupon.first_due_amount
        )}`,
        `2\u00B0 vencimiento: ${formatDateDisplay(coupon.second_due_date)} | ${formatCurrency(
          coupon.second_due_amount
        )}`,
        `3\u00B0 vencimiento: ${formatDateDisplay(coupon.third_due_date)} | ${formatCurrency(
          coupon.third_due_amount
        )}`,
      ];
    }

    const isCancelled = !!(
      coupon.cancellation_date && coupon.cancellation_date !== '0000-00-00 00:00:00'
    );

    return {
      uuid: coupon.uuid,
      orden: coupon.contract_number,
      suscriptores: coupon.subscribers.map(sub => `- ${sub.subscriber.last_name}, ${sub.subscriber.first_name}`),
      mes: formatDateDisplay(coupon.first_due_date, { options: { month: 'long' }, fallback: '' }),
      año: coupon.year,
      cuotas: cuotas,
      importes: importes,
      cancelado: !isCancelled ? (
          <Typography
            sx={{
              color: new Date() > new Date(coupon.third_due_date) ? 'red' : 'orange',
              ml: 1,
              mt: 1,
              fontWeight: 'medium',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 0.5,
            }}
          >
            {new Date() > new Date(coupon.third_due_date) ? (
              <EventBusyIcon fontSize="small" />
            ) : (
              <AccessTimeIcon fontSize="small" />
            )}
            No
          </Typography>
        ) : (
          <Typography sx={{ color: 'green', ml: 1, mt: 1, fontWeight: 'medium' }}>
            {formatDateDisplay(coupon.cancellation_date, { fallback: '' })}
          </Typography>
        ),
      can_delete: coupon.can_delete,
      isCancelled,
    };
  });
};

/**
 * Normaliza un valor a número
 * @param {any} value - Valor a normalizar
 * @returns {number} - Valor normalizado
 */
export const normalizeValue = (value) => {
  const parsedValue = parseFloat(value);
  return isNaN(parsedValue) ? 0 : parsedValue;
};

/**
 * Genera elementos de menú para años
 * @param {number} startYear - Año inicial (opcional, por defecto 1990)
 * @returns {Array} - Array de elementos MenuItem
 */
export const generateYearMenuItems = (startYear = 1990) => {
  const currentYear = new Date().getFullYear();
  let items = [];

  for (let i = currentYear; i >= startYear; i--) {
    items.push({
      key: i,
      value: i,
      label: i.toString(),
    });
  }

  return items;
};

/**
 * Valida entrada numérica para campos de adelanto
 * @param {string} value - Valor a validar
 * @returns {boolean} - True si es válido
 */
export const validateNumericInput = (value) => {
  return /^\d*\.?\d*$/.test(value) || value === '.' || value === '0.';
};
